import React, { useState, useRef, useEffect } from 'react';
import { MaxRectsPacker, Rectangle } from 'maxrects-packer';
import { ShieldCheck, Upload, Play, Shuffle, Download, X, Settings, ArrowLeft } from 'lucide-react';
import './Nesting.css';

type SvgItem = {
    id: string;
    fileContent: string;
    fileName: string;
    width: number;
    height: number;
    quantity: number;
    innerHtml: string;
};

export function Nesting() {
    const [items, setItems] = useState<SvgItem[]>([]);
    const [boardWidth, setBoardWidth] = useState(1300);
    const [boardHeight, setBoardHeight] = useState(900);
    const [margin, setMargin] = useState(3);
    const [allowRotation, setAllowRotation] = useState(true);
    const [bins, setBins] = useState<any[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const parseSvg = (content: string, name: string): SvgItem | null => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, 'image/svg+xml');
            const svgEl = doc.querySelector('svg');
            if (!svgEl) return null;

            let w = 0;
            let h = 0;

            if (svgEl.hasAttribute('viewBox')) {
                const vb = svgEl.getAttribute('viewBox')?.split(/[ ,]+/).map(Number) || [];
                w = vb[2];
                h = vb[3];
            } else if (svgEl.hasAttribute('width') && svgEl.hasAttribute('height')) {
                w = parseFloat(svgEl.getAttribute('width') || '0');
                h = parseFloat(svgEl.getAttribute('height') || '0');
            }

            if (!w || !h) {
                // Default fallback
                w = 100;
                h = 100;
            }

            return {
                id: Math.random().toString(36).substring(2, 9),
                fileContent: content,
                fileName: name,
                width: w,
                height: h,
                quantity: 1,
                innerHtml: svgEl.innerHTML
            };
        } catch (e) {
            console.error("Error parsing SVG:", e);
            return null;
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const newItems: SvgItem[] = [];
        let processed = 0;

        files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const content = event.target?.result as string;
                const parsed = parseSvg(content, file.name);
                if (parsed) newItems.push(parsed);

                processed++;
                if (processed === files.length) {
                    setItems(prev => [...prev, ...newItems]);
                }
            };
            reader.readAsText(file);
        });

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const updateQuantity = (id: string, qty: number) => {
        setItems(prev => prev.map(item => item.id === id ? { ...item, quantity: Math.max(1, qty) } : item));
    };

    const removeItem = (id: string) => {
        setItems(prev => prev.filter(item => item.id !== id));
    };

    const runNesting = () => {
        if (items.length === 0) return;
        setIsGenerating(true);

        setTimeout(() => {
            const packer = new MaxRectsPacker(boardWidth, boardHeight, margin, {
                smart: true,
                pot: false,
                square: false,
                allowRotation: allowRotation
            });

            const rectsToPack: any[] = [];
            items.forEach(item => {
                for (let i = 0; i < item.quantity; i++) {
                    rectsToPack.push({
                        width: item.width,
                        height: item.height,
                        data: {
                            id: item.id,
                            name: item.fileName,
                            innerHtml: item.innerHtml,
                            originalWidth: item.width,
                            originalHeight: item.height
                        }
                    });
                }
            });

            packer.addArray(rectsToPack);
            setBins(packer.bins);
            setIsGenerating(false);
        }, 300);
    };

    const downloadBin = (bin: any, index: number) => {
        let svgStr = `<svg width="${boardWidth}" height="${boardHeight}" viewBox="0 0 ${boardWidth} ${boardHeight}" xmlns="http://www.w3.org/2000/svg">\n`;
        // Board border
        svgStr += `  <rect x="0" y="0" width="${boardWidth}" height="${boardHeight}" fill="none" stroke="black" stroke-width="1" />\n`;

        bin.rects.forEach((rect: any) => {
            const isRotated = rect.rot;
            const item = rect.data;

            // Transform origin changes depending on rotation because 2D packers generally rotate around the top-left corner
            const tX = rect.x;
            const tY = rect.y;

            if (isRotated) {
                // If rotated 90 degrees, maxrects-packer defines rect.width as originalHeight, and rect.height as originalWidth
                // We need to translate and rotate correctly
                svgStr += `  <g transform="translate(${tX + rect.width}, ${tY}) rotate(90)">\n`;
            } else {
                svgStr += `  <g transform="translate(${tX}, ${tY})">\n`;
            }

            svgStr += `    <!-- ${item.name} -->\n`;
            // Bounding box for debug
            // svgStr += `    <rect width="${item.originalWidth}" height="${item.originalHeight}" fill="none" stroke="red" stroke-width="0.5" stroke-dasharray="2,2"/>\n`;
            svgStr += `    ${item.innerHtml}\n`;
            svgStr += `  </g>\n`;
        });

        svgStr += `</svg>`;

        const blob = new Blob([svgStr], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Nesting_Plancha_${index + 1}.svg`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="nesting-container">
            <header className="main-header" style={{ position: 'relative' }}>
                <div className="header-content">
                    <div className="logo-section">
                        <button className="icon-btn" onClick={() => window.location.href = '/'} style={{ marginRight: 12, border: '1px solid var(--border)' }}>
                            <ArrowLeft size={18} />
                        </button>
                        <ShieldCheck size={28} className="logo-icon" />
                        <h1>Anidado Automático CO₂ <span>[Experimental]</span></h1>
                    </div>
                    <div className="header-right">
                        <input
                            type="file"
                            accept=".svg"
                            multiple
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                        />
                        <button className="btn primary" onClick={() => fileInputRef.current?.click()}>
                            <Upload size={18} /> Subir SVG
                        </button>
                    </div>
                </div>
            </header>

            <div className="nesting-body">
                <aside className="nesting-sidebar">
                    <div className="settings-panel">
                        <h3><Settings size={18} /> Configuración de Plancha</h3>
                        <div className="setting-group">
                            <label>Ancho (mm)</label>
                            <input type="number" value={boardWidth} onChange={e => setBoardWidth(Number(e.target.value))} />
                        </div>
                        <div className="setting-group">
                            <label>Alto (mm)</label>
                            <input type="number" value={boardHeight} onChange={e => setBoardHeight(Number(e.target.value))} />
                        </div>
                        <div className="setting-group">
                            <label>Margen entre piezas (mm)</label>
                            <input type="number" value={margin} onChange={e => setMargin(Number(e.target.value))} />
                        </div>
                        <div className="setting-checkbox">
                            <label>
                                <input type="checkbox" checked={allowRotation} onChange={e => setAllowRotation(e.target.checked)} />
                                Permitir rotación (90°)
                            </label>
                        </div>
                    </div>

                    <div className="items-list">
                        <div className="list-header">
                            <h3>Piezas ({items.reduce((acc, a) => acc + a.quantity, 0)})</h3>
                        </div>
                        {items.length === 0 ? (
                            <p className="empty-text">No hay archivos. Sube algunos SVG para empezar.</p>
                        ) : (
                            <div className="items-scroll">
                                {items.map(item => (
                                    <div key={item.id} className="svg-item-card">
                                        <div className="svg-info">
                                            <span className="name" title={item.fileName}>{item.fileName}</span>
                                            <span className="dims">{Math.round(item.width)} x {Math.round(item.height)} mm</span>
                                        </div>
                                        <div className="svg-actions">
                                            <input type="number" value={item.quantity} onChange={e => updateQuantity(item.id, parseInt(e.target.value) || 1)} min="1" />
                                            <button onClick={() => removeItem(item.id)} className="icon-btn danger" title="Eliminar"><X size={14} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <button className="btn run-btn" onClick={runNesting} disabled={items.length === 0 || isGenerating}>
                        {isGenerating ? 'Generando...' : <><Play size={18} /> Procesar Nesting</>}
                    </button>
                </aside>

                <main className="nesting-preview">
                    {bins.length === 0 ? (
                        <div className="empty-preview">
                            <Shuffle size={48} />
                            <p>Organiza tus piezas SVG para corte láser CO₂.</p>
                            <span>Configura tus piezas a la izquierda y dale click a procesar.</span>
                        </div>
                    ) : (
                        <div className="bins-container">
                            <div className="bins-header">
                                <h2>Resultado: {bins.length} Plancha(s)</h2>
                            </div>
                            <div className="bins-grid">
                                {bins.map((bin, i) => (
                                    <div key={i} className="bin-card">
                                        <div className="bin-header">
                                            <h3>Plancha {i + 1}</h3>
                                            <div className="bin-stats">
                                                <span>{Math.round((bin.width * bin.height / (boardWidth * boardHeight)) * 100)}% usado</span>
                                                <button className="btn secondary small" onClick={() => downloadBin(bin, i)}>
                                                    <Download size={14} /> Exportar SVG
                                                </button>
                                            </div>
                                        </div>
                                        <div className="bin-canvas-container" style={{ aspectRatio: `${boardWidth}/${boardHeight}` }}>
                                            <svg viewBox={`0 0 ${boardWidth} ${boardHeight}`} width="100%" height="100%">
                                                <rect width={boardWidth} height={boardHeight} fill="#f8fafc" stroke="#cbd5e1" />
                                                {bin.rects.map((rect: any, j: number) => {
                                                    const isRotated = rect.rot;
                                                    const item = rect.data;
                                                    // Map colors by item ID for visually distinct pieces
                                                    const hue = (parseInt(item.id, 36) % 360);
                                                    const color = `hsla(${hue}, 70%, 50%, 0.2)`;
                                                    const stroke = `hsl(${hue}, 70%, 40%)`;

                                                    return (
                                                        <g key={j} transform={`translate(${rect.x}, ${rect.y})`}>
                                                            <rect
                                                                width={rect.width}
                                                                height={rect.height}
                                                                fill={color}
                                                                stroke={stroke}
                                                                strokeWidth="2"
                                                            />
                                                            <text x="5" y="15" fontSize="10" fill={stroke} fontWeight="bold">
                                                                {isRotated ? 'R' : ''} {item.name.substring(0, 10)}
                                                            </text>
                                                        </g>
                                                    )
                                                })}
                                            </svg>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
